import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiBell,
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
  FiX,
  FiChevronRight,
  FiLoader,
} from "react-icons/fi";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";

interface Notification {
  id: number;
  icon: string;
  iconBg: string;
  title: string;
  message: string;
  time: string;
  type: "alert" | "achievement" | "info" | "report";
  childName?: string;
  read: boolean;
  link?: string;
}

const Notifications: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications from backend
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user?.email) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Try to fetch notifications - adjust endpoint as needed
        try {
          const response = await axios.get(`${API_BASE_URL}/parents/notifications`, {
            params: { parentEmail: user.email },
          });
          if (response.data && Array.isArray(response.data)) {
            setNotifications(response.data);
          }
        } catch (err: any) {
          // If endpoint doesn't exist, use empty array
          console.log("Notifications endpoint not available");
          setNotifications([]);
        }
      } catch (err: any) {
        console.error("Error fetching notifications:", err);
        setError("Failed to load notifications");
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [user]);



  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = async (id: number) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    // Try to update on backend
    try {
      await axios.put(`${API_BASE_URL}/parents/notifications/${id}/read`);
    } catch (err) {
      console.log("Notification update endpoint not available");
    }
  };

  const markAllAsRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    // Try to update on backend
    try {
      await axios.put(`${API_BASE_URL}/parents/notifications/read-all`, {
        parentEmail: user?.email,
      });
    } catch (err) {
      console.log("Notification update endpoint not available");
    }
  };

  const deleteNotification = async (id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    // Try to delete on backend
    try {
      await axios.delete(`${API_BASE_URL}/parents/notifications/${id}`);
    } catch (err) {
      console.log("Notification delete endpoint not available");
    }
  };

  if (loading) {
    return (
      <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FiLoader className="mx-auto text-[#1ecab8] mb-4 animate-spin" size={48} />
          <p className="text-slate-600">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Notifications</h1>
            <p className="text-slate-600">
              Stay updated on your children's wellbeing and activities
            </p>
          </div>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">
              {error}
            </div>
          )}
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="px-4 py-2 bg-[#1ecab8] text-white rounded-lg font-medium hover:bg-[#1bb8a6] transition-colors"
            >
              Mark All as Read
            </button>
          )}
        </div>
        {unreadCount > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
            You have {unreadCount} unread notification{unreadCount !== 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 overflow-hidden">
        {notifications.length === 0 ? (
          <div className="p-12 text-center">
            <FiBell size={48} className="mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600">No notifications yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                className={`p-6 hover:bg-slate-50 transition-colors ${
                  !notif.read ? "bg-blue-50/50" : ""
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: notif.iconBg }}
                  >
                    {notif.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{notif.title}</h3>
                          {!notif.read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{notif.message}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>{notif.time}</span>
                          {notif.childName && (
                            <>
                              <span>•</span>
                              <span>Child: {notif.childName}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {notif.link && (
                          <Link
                            to={notif.link}
                            onClick={() => markAsRead(notif.id)}
                            className="text-[#1ecab8] hover:text-[#1bb8a6] transition-colors"
                          >
                            <FiChevronRight size={20} />
                          </Link>
                        )}
                        <button
                          onClick={() => deleteNotification(notif.id)}
                          className="text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          <FiX size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;

